/* global GIT_SHA */
import LogRocket from 'logrocket';
import _isObject from 'lodash/isObject';

const CP_AUTH_HEADER = 'CP-Authorization';
const SANITIZED_STRING = '**SANITIZED**';
const PRIVATE_FIELDS_REGEX = /(key|password|token|client_id)/i;

// Session sampling to avoid 429 from e.logrocket.com/api/3/store/ (LogRocket ingestion rate limit).
// 429s occur when too many events (network, DOM, console) are sent; sampling reduces uploads.
// Optional: set window.LOGROCKET_SAMPLE_RATE (0–1) via server pageData to override default 0.5.
// If 429s persist, consider network: { isEnabled: false } or console: { isEnabled: false } in init.
const LOGROCKET_SAMPLE_RATE =
  typeof window !== 'undefined' && typeof window.LOGROCKET_SAMPLE_RATE === 'number'
    ? Math.min(1, Math.max(0, window.LOGROCKET_SAMPLE_RATE))
    : 0.5;
const shouldSendLogRocketData = typeof window !== 'undefined' ? Math.random() < LOGROCKET_SAMPLE_RATE : true;

export const findAndSanitizeDeep = path => {
  if (!_isObject(path)) return;

  // eslint-disable-next-line no-restricted-syntax
  for (const bodyKey in path) {
    if (_isObject(path[bodyKey])) {
      findAndSanitizeDeep(path[bodyKey]);
    } else if (PRIVATE_FIELDS_REGEX.test(bodyKey)) {
      path[bodyKey] = SANITIZED_STRING;
    }
  }

  return path;
};

export const sanitizeHTTPBody = body => {
  let parsedBody;
  try {
    parsedBody = JSON.parse(body);
  } catch (e) {
    return body;
  }

  return parsedBody && typeof parsedBody === 'object' ? JSON.stringify(findAndSanitizeDeep(parsedBody)) : body;
};

export const initializeLogRocket = () => {
  const logRocketProjectKey = window.LOGROCKET_PROJECT_KEY;
  if (!logRocketProjectKey) return;

  LogRocket.init(logRocketProjectKey, {
    release: `${GIT_SHA}`,
    // Reduces traffic to e.logrocket.com/store to avoid 429 (rate limit). When false, session is still
    // recorded locally but not uploaded.
    shouldSendData: () => shouldSendLogRocketData,
    network: {
      requestSanitizer: request => {
        if (request.headers[CP_AUTH_HEADER]) {
          request.headers[CP_AUTH_HEADER] = SANITIZED_STRING;
        }

        const sanitizedBody = sanitizeHTTPBody(request.body);
        request.body = sanitizedBody;
        return request;
      },
      responseSanitizer: response => {
        const sanitizedBody = sanitizeHTTPBody(response.body);
        response.body = sanitizedBody;
        return response;
      },
    },
  });
};

const trackOptimizelyExperimentInLogRocket = experiment => {
  if (window.optimizely && window.optimizely.get) {
    const targetExperiment = window.optimizely.get('state').getExperimentStates()[experiment.id] || null;
    if (!targetExperiment || !targetExperiment.isActive || targetExperiment.isInExperimentHoldback) return;
    if (_isObject(targetExperiment.variation)) {
      const { id, name } = targetExperiment.variation;
      const variationId = id || '';
      const variationName = name || '';
      LogRocket.track(experiment.id, {
        variationId,
        variationName,
        isExperimentActive: targetExperiment.isActive,
      });
      LogRocket.track(experiment.name, {
        variationId,
        variationName,
        isExperimentActive: targetExperiment.isActive,
      });
    } else {
      LogRocket.track(experiment.id, {
        isExperimentActive: targetExperiment.isActive,
      });
      LogRocket.track(experiment.name, {
        isExperimentActive: targetExperiment.isActive,
      });
    }
  }
};

export const initializeLogRocketForOptimizely = () => {
  if (window.optimizely && window.optimizely.get) {
    const experiments = window.optimizely.get('state').getExperimentStates();
    Object.values(experiments).forEach(experiment => {
      if (experiment.isActive && !experiment.isInExperimentHoldback) {
        trackOptimizelyExperimentInLogRocket(experiment);
      }
    });
  }
};

export const captureException = LogRocket.captureException.bind(LogRocket);
export const captureMessage = LogRocket.captureMessage.bind(LogRocket);
